import { useEffect, useState } from 'react';

interface Props {
  /**
   * Ordered list of frame URLs to cycle through (e.g. 4 idle-breathing frames
   * for the south-facing pose). If just one URL is passed, behaves as a static
   * image.
   */
  frames: string[];
  /** ms per frame. PixelLab's breathing-idle is 4 frames; 180 ms/frame ≈ 0.7 s loop. */
  frameMs?: number;
  /** Optional rim-light colour around the frame. */
  glowColor?: string;
  width?: number;
  height?: number;
}

/**
 * Pixel-art alternative to CharacterPortrait3D. Cycles through the supplied
 * frame URLs with `image-rendering: pixelated` so the original 64-ish-px sprite
 * stays crisp at portrait size. Pre-loads all frames once on mount to avoid
 * flicker during the cycle.
 */
export function CharacterPortraitPixel({
  frames, frameMs = 180, glowColor, width = 180, height = 240,
}: Props) {
  const [frameIdx, setFrameIdx] = useState(0);

  // Pre-load every frame as soon as the URL list changes — without this the
  // browser fetches each frame on first display, which causes a brief flash.
  useEffect(() => {
    for (const url of frames) {
      const img = new Image();
      img.src = url;
    }
  }, [frames]);

  useEffect(() => {
    if (frames.length <= 1) return;
    const id = window.setInterval(() => {
      setFrameIdx((i) => (i + 1) % frames.length);
    }, frameMs);
    return () => window.clearInterval(id);
  }, [frames, frameMs]);

  const src = frames[Math.min(frameIdx, frames.length - 1)] ?? '';

  return (
    <div
      style={{
        width,
        height,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: glowColor
          ? `radial-gradient(ellipse at center, ${glowColor}33 0%, transparent 70%)`
          : 'transparent',
      }}
    >
      <img
        src={src}
        alt="character"
        style={{
          maxWidth: '90%',
          maxHeight: '90%',
          imageRendering: 'pixelated',
          objectFit: 'contain',
        }}
        draggable={false}
      />
    </div>
  );
}
